import React, { useState } from "react";
import { Plus, Trash2, Settings, GripVertical, Upload, Check, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { useList } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const ROLES = ["admin", "production_manager", "supervisor", "operator", "qc", "packing_staff", "viewer", "buyer"];

// ---- Simple name-based master list ----
function SimpleMaster({ coll, label }) {
  const qc = useQueryClient();
  const { data: items, isLoading } = useList(coll, `/${coll}`);
  const [name, setName] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post(`/${coll}`, { name: name.trim() }); setName(""); qc.invalidateQueries(); toast.success(`${label} added`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { try { await api.delete(`/${coll}/${id}`); qc.invalidateQueries(); } catch { toast.error("Failed"); } };
  if (isLoading) return <Loading />;
  return (
    <div>
      <div className="flex gap-2 mb-4 max-w-md">
        <Input placeholder={`New ${label}…`} value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} data-testid={`${coll}-input`} />
        <Button onClick={add} className="gap-1" data-testid={`${coll}-add-btn`}><Plus className="w-4 h-4" /> Add</Button>
      </div>
      {!items?.length ? <EmptyState title={`No ${label}`} /> : (
        <div className="flex flex-wrap gap-2">
          {items.map((it) => (
            <div key={it.id} className="flex items-center gap-2 pl-3 pr-1.5 py-1.5 rounded-lg bg-secondary" data-testid={`${coll}-item-${it.id}`}>
              <span className="text-sm font-medium">{it.name}</span>
              <button onClick={() => del(it.id)} className="text-muted-foreground hover:text-destructive p-0.5"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Production stages (order + active toggle) ----
function StagesMaster() {
  const qc = useQueryClient();
  const { data: stages, isLoading } = useList("stages", "/stages");
  const [name, setName] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/stages", { name: name.trim(), order: (stages?.length || 0) + 1 }); setName(""); qc.invalidateQueries(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggle = async (s) => { await api.put(`/stages/${s.id}`, { active: !s.active }); qc.invalidateQueries(); };
  const del = async (id) => { await api.delete(`/stages/${id}`); qc.invalidateQueries(); };
  if (isLoading) return <Loading />;
  return (
    <div>
      <div className="flex gap-2 mb-4 max-w-md">
        <Input placeholder="New stage…" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="stage-input" />
        <Button onClick={add} className="gap-1" data-testid="stage-add-btn"><Plus className="w-4 h-4" /> Add</Button>
      </div>
      <div className="space-y-1.5 max-w-xl">
        {(stages || []).map((s) => (
          <div key={s.id} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50" data-testid={`stage-item-${s.id}`}>
            <GripVertical className="w-4 h-4 text-muted-foreground" />
            <span className="font-num text-xs text-muted-foreground w-6">{s.order}</span>
            <span className="flex-1 font-medium text-sm">{s.name}</span>
            <Switch checked={s.active} onCheckedChange={() => toggle(s)} data-testid={`stage-toggle-${s.id}`} />
            <button onClick={() => del(s.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Product master ----
function ProductsMaster() {
  const qc = useQueryClient();
  const { data: products, isLoading } = useList("products", "/products");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { name: "", sku: "", category: "", fabric: "", gsm: "", default_color: "", description: "", image_url: "" };
  const [f, setF] = useState(blank);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const openNew = () => { setEditing(null); setF(blank); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setF({ ...blank, ...p }); setOpen(true); };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set("image_url", `${process.env.REACT_APP_BACKEND_URL}${data.url}`); toast.success("Uploaded"); }
    catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!f.name) return toast.error("Name required");
    try {
      if (editing) await api.put(`/products/${editing.id}`, f);
      else await api.post("/products", f);
      toast.success(editing ? "Product updated (reflected everywhere)" : "Product created");
      qc.invalidateQueries(); setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete product?")) return; await api.delete(`/products/${id}`); qc.invalidateQueries(); };

  if (isLoading) return <Loading />;
  return (
    <div>
      <Button onClick={openNew} className="gap-2 mb-4" data-testid="new-product-btn"><Plus className="w-4 h-4" /> New Product</Button>
      {!products?.length ? <EmptyState title="No products" /> : (
        <div className="overflow-x-auto"><Table>
          <TableHeader><TableRow><TableHead>Image</TableHead><TableHead>Name</TableHead><TableHead>SKU</TableHead><TableHead>Category</TableHead><TableHead>Fabric</TableHead><TableHead>GSM</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>{products.map((p) => (
            <TableRow key={p.id} data-testid={`product-row-${p.sku}`}>
              <TableCell>{p.image_url ? <img src={p.image_url} alt="" className="w-9 h-9 rounded object-cover" /> : <div className="w-9 h-9 rounded bg-secondary" />}</TableCell>
              <TableCell className="font-medium">{p.name}</TableCell><TableCell className="font-num text-sm">{p.sku}</TableCell>
              <TableCell className="text-sm">{p.category}</TableCell><TableCell className="text-sm">{p.fabric}</TableCell><TableCell className="font-num">{p.gsm}</TableCell>
              <TableCell><div className="flex gap-1"><Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-product-${p.sku}`}>Edit</Button><Button size="icon" variant="ghost" onClick={() => del(p.id)}><Trash2 className="w-4 h-4" /></Button></div></TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></div>
      )}
      <Dialog open={open} onOpenChange={setOpen}><DialogContent data-testid="product-form">
        <DialogHeader><DialogTitle>{editing ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="product-name-input" /></div>
          <div><Label>SKU</Label><Input value={f.sku} onChange={(e) => set("sku", e.target.value)} /></div>
          <div><Label>Category</Label><Input value={f.category} onChange={(e) => set("category", e.target.value)} /></div>
          <div><Label>Fabric</Label><Input value={f.fabric} onChange={(e) => set("fabric", e.target.value)} /></div>
          <div><Label>GSM</Label><Input value={f.gsm} onChange={(e) => set("gsm", e.target.value)} /></div>
          <div className="col-span-2"><Label>Default Color</Label><Input value={f.default_color} onChange={(e) => set("default_color", e.target.value)} /></div>
          <div className="col-span-2"><Label>Image</Label>
            <div className="flex items-center gap-2">
              <input type="file" accept="image/*" onChange={upload} className="hidden" id="prod-master-img" />
              <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById("prod-master-img").click()} disabled={uploading} className="gap-1"><Upload className="w-3.5 h-3.5" /> {uploading ? "…" : "Upload"}</Button>
              {f.image_url && <img src={f.image_url} alt="" className="w-9 h-9 rounded object-cover" />}
            </div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} data-testid="product-save-btn">Save</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}

// ---- Customers master ----
function CustomersMaster() {
  const qc = useQueryClient();
  const { data: customers, isLoading } = useList("customers", "/customers");
  const [f, setF] = useState({ name: "", contact_person: "", email: "", country: "" });
  const add = async () => { if (!f.name) return toast.error("Name required"); await api.post("/customers", f); setF({ name: "", contact_person: "", email: "", country: "" }); qc.invalidateQueries(); toast.success("Customer added"); };
  const del = async (id) => { await api.delete(`/customers/${id}`); qc.invalidateQueries(); };
  if (isLoading) return <Loading />;
  return (
    <div>
      <div className="grid sm:grid-cols-4 gap-2 mb-4">
        <Input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="customer-name-input" />
        <Input placeholder="Contact" value={f.contact_person} onChange={(e) => setF({ ...f, contact_person: e.target.value })} />
        <Input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <div className="flex gap-2"><Input placeholder="Country" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} /><Button onClick={add} data-testid="customer-add-btn"><Plus className="w-4 h-4" /></Button></div>
      </div>
      {!customers?.length ? <EmptyState title="No customers" /> : (
        <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Email</TableHead><TableHead>Country</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>{customers.map((c) => (
            <TableRow key={c.id}><TableCell className="font-medium">{c.name}</TableCell><TableCell className="text-sm">{c.contact_person}</TableCell><TableCell className="text-sm">{c.email}</TableCell><TableCell className="text-sm">{c.country}</TableCell><TableCell><Button size="icon" variant="ghost" onClick={() => del(c.id)}><Trash2 className="w-4 h-4" /></Button></TableCell></TableRow>
          ))}</TableBody>
        </Table>
      )}
    </div>
  );
}

// ---- Users & roles ----
function UsersMaster() {
  const qc = useQueryClient();
  const { data: users, isLoading } = useList("users", "/users");
  const { data: customers = [] } = useList("customers", "/customers");
  const [pwUser, setPwUser] = useState(null);
  const setRole = async (uid, role) => { try { await api.put(`/users/${uid}/role`, { role }); qc.invalidateQueries(); toast.success("Role updated"); } catch { toast.error("Failed"); } };
  const setBuyer = async (uid, linked_buyer) => { try { await api.put(`/users/${uid}/link`, { linked_buyer: linked_buyer === "__none__" ? null : linked_buyer }); qc.invalidateQueries(); toast.success("Buyer link updated"); } catch { toast.error("Failed"); } };
  const setStatus = async (uid, status) => { try { await api.put(`/users/${uid}/status`, { status }); qc.invalidateQueries(); toast.success(status === "approved" ? "Access approved" : "Access revoked"); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  if (isLoading) return <Loading />;
  const pending = (users || []).filter((u) => u.status === "pending");
  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">New sign-ins (Google or email/password) start as <span className="font-semibold text-warning">pending</span> with no access. Approve them here, set their role, and (for buyers) link a customer. You can also set or reset a user's password.</p>
      {pending.length > 0 && <div className="mb-3 text-sm font-semibold text-warning" data-testid="pending-count">{pending.length} account(s) awaiting approval</div>}
      {!users?.length ? <EmptyState title="No users" /> : (
        <Table><TableHeader><TableRow><TableHead>User</TableHead><TableHead>Email</TableHead><TableHead>Access</TableHead><TableHead>Role</TableHead><TableHead>Linked Buyer</TableHead></TableRow></TableHeader>
          <TableBody>{users.map((u) => (
            <TableRow key={u.user_id} data-testid={`user-row-${u.email}`} className={u.status === "pending" ? "bg-warning/5" : ""}>
              <TableCell className="font-medium"><div>{u.name || "—"}</div><button className="text-[11px] text-primary hover:underline mt-0.5 flex items-center gap-1" onClick={() => setPwUser(u)} data-testid={`set-password-${u.email}`}><KeyRound className="w-3 h-3" /> Set password</button></TableCell><TableCell className="text-sm">{u.email}</TableCell>
              <TableCell>
                {u.status === "pending" ? (
                  <Button size="sm" onClick={() => setStatus(u.user_id, "approved")} data-testid={`approve-user-${u.email}`} className="h-8 gap-1"><Check className="w-3.5 h-3.5" /> Approve</Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-success"><Check className="w-3.5 h-3.5" /> Approved</span>
                    <Button size="sm" variant="ghost" onClick={() => setStatus(u.user_id, "pending")} data-testid={`revoke-user-${u.email}`} className="h-7 text-xs text-destructive">Revoke</Button>
                  </div>
                )}
              </TableCell>
              <TableCell><Select value={u.role} onValueChange={(v) => setRole(u.user_id, v)}><SelectTrigger className="w-44" data-testid={`user-role-${u.email}`}><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></TableCell>
              <TableCell>
                <Select value={u.linked_buyer || "__none__"} onValueChange={(v) => setBuyer(u.user_id, v)} disabled={u.role !== "buyer"}>
                  <SelectTrigger className="w-48" data-testid={`user-buyer-${u.email}`}><SelectValue placeholder="Not linked" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Not linked</SelectItem>
                    {customers.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </TableCell>
            </TableRow>
          ))}</TableBody>
        </Table>
      )}
      {pwUser && <ResetPwDialog u={pwUser} onClose={() => setPwUser(null)} />}
    </div>
  );
}

function ResetPwDialog({ u, onClose }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    try { await api.put(`/users/${u.user_id}/password`, { password: pw }); toast.success("Password updated"); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="set-password-dialog">
        <DialogHeader><DialogTitle>Set password</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Set a new password for <span className="font-medium">{u.email}</span>. Share it with them securely — they can change it later.</p>
        <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (min 6 chars)" data-testid="set-password-input" />
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy} data-testid="set-password-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TABS = [
  { v: "products", label: "Products", el: <ProductsMaster /> },
  { v: "customers", label: "Customers", el: <CustomersMaster /> },
  { v: "stages", label: "Stages", el: <StagesMaster /> },
  { v: "printing_types", label: "Printing Types", el: <SimpleMaster coll="printing_types" label="Printing Type" /> },
  { v: "sample_types", label: "Sample Types", el: <SimpleMaster coll="sample_types" label="Sample Type" /> },
  { v: "defect_types", label: "Defect Types", el: <SimpleMaster coll="defect_types" label="Defect Type" /> },
  { v: "departments", label: "Departments", el: <SimpleMaster coll="departments" label="Department" /> },
  { v: "shifts", label: "Shifts", el: <SimpleMaster coll="shifts" label="Shift" /> },
  { v: "users", label: "Users & Roles", el: <UsersMaster /> },
];

export default function AdminSettings() {
  const { user } = useAuth();
  const [tab, setTab] = useState("products");
  if (user?.role !== "admin") return <EmptyState title="Admins only" hint="You do not have access to master settings." />;
  return (
    <div>
      <PageHeader title="Admin Master Settings" subtitle="Manage masters — changes reflect across the whole system" testid="admin-header" />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 mb-4">
          {TABS.map((t) => <TabsTrigger key={t.v} value={t.v} data-testid={`admin-tab-${t.v}`}>{t.label}</TabsTrigger>)}
        </TabsList>
        {TABS.map((t) => <TabsContent key={t.v} value={t.v}><Card className="p-5"><h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4 flex items-center gap-2"><Settings className="w-4 h-4" /> {t.label}</h3>{tab === t.v && t.el}</Card></TabsContent>)}
      </Tabs>
    </div>
  );
}
